import React, { useState, useEffect } from 'react';
import { ClipboardPaste, MousePointerSquareDashed, Eye } from 'lucide-react';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';

interface CanvasContextMenuProps {
  children: React.ReactNode;
  onPaste: (position: { x: number; y: number }) => void;
  onSelectAll: () => void;
  onRescueStrandedItems: () => void;
  getClickPosition: (e: React.MouseEvent) => { x: number; y: number };
}

export function CanvasContextMenu({ 
  children, 
  onPaste, 
  onSelectAll,
  onRescueStrandedItems,
  getClickPosition 
}: CanvasContextMenuProps) {
  const [clipboardHasContent, setClipboardHasContent] = useState(false);
  const [lastClickPosition, setLastClickPosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const checkClipboard = async () => {
      try {
        // Check for text content
        const text = await navigator.clipboard.readText();
        setClipboardHasContent(!!text);
      } catch {
        // Clipboard access might be denied
        setClipboardHasContent(false);
      }
    };
    
    // Check clipboard when component mounts and on focus
    checkClipboard();
    window.addEventListener('focus', checkClipboard);
    return () => window.removeEventListener('focus', checkClipboard);
  }, []);

  const handleContextMenu = (e: React.MouseEvent) => {
    setLastClickPosition(getClickPosition(e));
  };

  const handlePaste = () => {
    onPaste(lastClickPosition);
  };

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild onContextMenu={handleContextMenu}>
        {children}
      </ContextMenuTrigger>
      <ContextMenuContent className="w-48">
        <ContextMenuItem 
          onClick={handlePaste}
          disabled={!clipboardHasContent}
        >
          <ClipboardPaste className="mr-2 h-4 w-4" />
          <span>Paste</span>
          <span className="ml-auto text-xs text-muted-foreground">⌘V</span>
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem onClick={onSelectAll}>
          <MousePointerSquareDashed className="mr-2 h-4 w-4" />
          <span>Select All</span>
          <span className="ml-auto text-xs text-muted-foreground">⌘A</span>
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem onClick={onRescueStrandedItems}>
          <Eye className="mr-2 h-4 w-4" />
          <span>Rescue hidden items</span>
          <span className="ml-auto text-xs text-muted-foreground">⇧⌘R</span>
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}
